import { Navigate, useLocation } from "react-router-dom";

import { isModuleAuthorized, moduleLandingPath, navCallerOf } from "@/lib/module-registry";
import { MODULES } from "@/lib/modules";
import { useAuthSession } from "@/lib/session-context";
import { isRouteAuthorized, type AppRoute } from "@/lib/routes";

interface ProtectedRouteProps {
  route: AppRoute;
  children: React.ReactNode;
}

/**
 * Guards a route against direct-URL access by unauthorized callers. The
 * `<AppShell>` already holds rendering until `/api/session` resolves, so by
 * the time this runs the session is guaranteed present — we only check
 * permission / feature / super-admin authorization here.
 *
 * Security is still enforced server-side — this only keeps unauthorized
 * users from seeing a page that would 403 them anyway, which is a better UX.
 */
export function ProtectedRoute({ route, children }: ProtectedRouteProps) {
  const session = useAuthSession();
  const { pathname } = useLocation();

  // A module's own page also needs the module's access permission — the same
  // gate that hides the module from the switcher. Shared record routes carry
  // no `moduleKey`, so they are gated by their own permissions alone.
  const mod = route.moduleKey ? MODULES.find((m) => m.key === route.moduleKey) : undefined;
  const moduleDenied =
    mod !== undefined &&
    !isModuleAuthorized(mod, session.permissions, session.is_super_admin);

  if (
    moduleDenied ||
    !isRouteAuthorized(
      route,
      session.is_super_admin,
      session.features,
      session.permissions,
      session.is_supplier,
    )
  ) {
    // A page the caller can't open inside a module they CAN open lands them
    // on that module's landing — its default page, or the
    // first one they can open — not out of the module entirely (AB#8596).
    // The landing is judged by the same `isRouteAuthorized`, so it renders
    // rather than bouncing again; the pathname check is the backstop against
    // a landing that is this page.
    const landing =
      mod && !moduleDenied
        ? moduleLandingPath(mod, navCallerOf(session))
        : undefined;
    if (landing && landing !== pathname) {
      return <Navigate to={landing} replace />;
    }

    // Otherwise the dashboard-first home: the caller can't open the blocked
    // route's module, or nothing in it, or it belongs to no module.
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
